import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-68970b09"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 1 }
const _hoisted_2 = { key: 2 }
const _hoisted_3 = /*#__PURE__*/_createTextVNode(" No access to camera.")
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_5 = {
  key: 3,
  class: "scanner-control"
}

import {
  onMounted,
  onBeforeUnmount,
  watch,
  ref,
} from 'vue';
import { throttle } from '@/utilities';

import {
  createWorker,
  destroyWorker,
  registerMessageListener,
  sendDecodeMessage,
} from '@/core/qr-recognition.worker-api';
import {
  isMediaStreamAPISupported as _isMediaStreamAPISupported,
  getCameraStream,
  enumerateDevices,
} from '@/core/mediaStreamAPI';
import Select from './ui/Select.vue';

export type Props = {
  class?: string;
};

type Device = {
  value: string;
  label: string;
};


export default /*#__PURE__*/_defineComponent({
  props: {
    class: null
  },
  emits: ["decode", "error"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'decode', data: string): void;
  (event: 'error', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  class?: string;
}





const isMounted = ref(false);
const isMediaStreamAPISupported = ref(_isMediaStreamAPISupported());
const isCameraAllowed = ref(true);
const cameraStreamError = ref('');
const cameras = ref<Device[]>([]);
const selectedCamera = ref({ value: '', label: '' });
const isFrontCamera = ref(false);

const throttledSendMessage = ref<(message: ImageData) => void>(() => {
  console.log('no camera');
});

const startVideoStream = async (currentCamera: Device) => {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  const video = document.createElement('video');

  const tick = () => {
    if (!isMounted.value) return;
    if (currentCamera !== selectedCamera.value) return;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      throttledSendMessage.value(imageData);
    }
    requestAnimationFrame(tick);
  };

  try {
    const width = 1080;
    const aspectRatio = window.innerWidth / window.innerHeight;
    const stream = await getCameraStream({
      deviceId: currentCamera.value,
      idealWidth: width,
      idealHeight: Math.round(width / aspectRatio),
      aspectRatio: 1 / aspectRatio,
    });

    const streamTrack = stream.getTracks()[0];

    stopCamera.value = streamTrack.stop.bind(streamTrack);
    isCameraAllowed.value = true;

    video.srcObject = stream;
    video.setAttribute('playsinline', 'true'); // required to tell iOS safari we don't want fullscreen
    video.play();
    requestAnimationFrame(tick);
  } catch (e) {
    console.log('Error on getCameraStream: ', e);
    isCameraAllowed.value = false;
    cameraStreamError.value = e as string;
  }
};

const stopCamera = ref(() => {
  console.log('stop camera');
});

onMounted(async () => {
  isMounted.value = true;

  if (!isMediaStreamAPISupported.value) {
    return;
  }
  try {
    createWorker();
    throttledSendMessage.value = throttle(sendDecodeMessage, 200);
    registerMessageListener((code) => {
      if (code) {
        emit('decode', code.data);
      } else {
        emit('error', 'not found');
      }
    });

    const newDevices = await enumerateDevices();
    const _cameras: { value: string; label: string }[] = [];
    newDevices.forEach((device, idx) => {
      if (device.kind === 'videoinput') {
        _cameras.push({
          value: device.deviceId,
          label: device.label || `video input ${idx}`,
        });
      }
    });
    cameras.value = _cameras;

    if (cameras.value.length) {
      const rearCamera = cameras.value.find((d) =>
        d.label.toLowerCase().includes('back')
      );

      if (rearCamera) {
        selectedCamera.value = rearCamera;
      } else {
        selectedCamera.value = cameras.value[0];
      }
    }
  } catch (e) {
    console.error(e);
  }
});

onBeforeUnmount(() => {
  isMounted.value = false;
  stopCamera.value();
  destroyWorker();
});

const onCameraSelect = (deviceId: string) => {
  const camera = cameras.value.find((c) => c.value === deviceId);
  if (!camera) return;

  selectedCamera.value = camera;
};

watch(selectedCamera, () => {
  stopCamera.value();
  startVideoStream(selectedCamera.value);

  const cameraLabel = selectedCamera.value.label.toLowerCase();
  isFrontCamera.value =
    cameraLabel.includes('front') || cameraLabel.includes('facetime');
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([`scanner-container`, props.class])
  }, [
    (isMediaStreamAPISupported.value)
      ? (_openBlock(), _createElementBlock("canvas", {
          key: 0,
          id: "canvas",
          style: _normalizeStyle({ transform: isFrontCamera.value ? `scaleX(-1)` : `scaleX(1)` })
        }, null, 4))
      : (_openBlock(), _createElementBlock("p", _hoisted_1, "Media stream API is not supported.")),
    (!isCameraAllowed.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
          _hoisted_3,
          _hoisted_4,
          _createTextVNode(_toDisplayString(cameraStreamError.value), 1)
        ]))
      : _createCommentVNode("", true),
    (cameras.value.length > 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(Select, {
            value: selectedCamera.value.value,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (onCameraSelect($event.target.value))),
            options: cameras.value,
            placeholder: "Select videoInput device"
          }, null, 8, ["value", "options"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})